import { ICPaasConfig, VoiceCallOptions, TextWidgetOptions, TextWidgetHandle, EventType, EventHandler, UsageQuery, UsageTotalsQuery } from './types';
import type { Session, UsageRecord, UsageTotals } from './protocol';
import { AuthManager } from './auth';
import { VoiceManager } from './voice';
import { TextManager } from './text';
import { APIClient } from './api';
import { EventEmitter } from './events';

/**
 * Main ICPaaS AI SDK client
 * 
 * @example
 * ```typescript
 * import { ICPaasAI } from '@icpaas/ai-sdk';
 * 
 * // In a web page use the publishable pk_ key; sk_ is for servers only.
 * const ai = new ICPaasAI({
 *   apiKey: 'pk_your_publishable_key',
 *   baseURL: 'https://your-instance.com'
 * });
 * 
 * await ai.login();
 * await ai.startVoiceCall({ agentId: 'your-agent-id', language: 'ENG' });
 * ```
 */
export class ICPaasAI extends EventEmitter {
  private auth: AuthManager;
  private voice: VoiceManager;
  private text: TextManager;
  private api: APIClient;
  private config: ICPaasConfig;

  constructor(config: ICPaasConfig) {
    super();

    if (!config.apiKey) {
      throw new Error('API key is required');
    }

    // pk_ is the publishable key for web pages: it can start voice and text
    // conversations only. sk_ is the full key, for servers.
    if (!config.apiKey.startsWith('sk_') && !config.apiKey.startsWith('pk_')) {
      throw new Error('Invalid API key format. API keys start with "pk_" (web pages) or "sk_" (servers)');
    }

    this.config = config;
    this.auth = new AuthManager(config);
    this.api = new APIClient(config, this.auth);
    this.voice = new VoiceManager(config, this.auth, this);
    this.text = new TextManager(config, this.auth, this);

    if (config.debug) {
      console.log('[ICPaaS SDK] Initialized with config:', {
        baseURL: config.baseURL,
        debug: config.debug,
      });
    }
  }

  // ==================== Authentication ====================

  /**
   * Authenticate with the API key and establish a session
   */
  async login(): Promise<void> {
    return this.auth.login();
  }

  /**
   * Clear the session
   */
  async logout(): Promise<void> {
    return this.auth.logout();
  }

  /**
   * Check if currently authenticated
   */
  isAuthenticated(): boolean {
    return this.auth.isAuthenticated();
  }

  // ==================== Voice ====================

  /**
   * Start a voice call
   * 
   * @example
   * ```typescript
   * await ai.startVoiceCall({
   *   agentId: 'your-agent-id', // or persona: 'IND_Male' for a built-in one
   *   language: 'IND',
   *   onStateChange: (state) => console.log('State:', state),
   *   onCallEnd: (session) => console.log('Call ended:', session)
   * });
   * ```
   */
  async startVoiceCall(options: VoiceCallOptions = {}): Promise<void> {
    await this.ensureAuthenticated();
    return this.voice.startCall(options);
  }

  /**
   * End the current voice call
   */
  endVoiceCall(): void {
    this.voice.endCall();
  }

  /**
   * Get current voice state
   */
  getVoiceState() {
    return this.voice.getState();
  }

  /**
   * Check if voice call is active
   */
  isVoiceCallActive(): boolean {
    return this.voice.isActive();
  }

  /**
   * Live AnalyserNodes for the caller's mic and the agent's voice, so a host
   * page can drive its own visualiser off the real signal. Null outside a call.
   */
  getVoiceAnalysers(): { mic: AnalyserNode | null; ai: AnalyserNode | null } {
    return { mic: this.voice.micAnalyser, ai: this.voice.aiAnalyser };
  }

  // ==================== Text ====================

  /**
   * Create a text chat widget. Without `targetElement` it mounts a floating
   * launcher button in the bottom-right corner. Each widget keeps its own
   * conversation history (last 20 turns).
   * 
   * @example
   * ```typescript
   * const chat = ai.createTextWidget({
   *   agentId: 'your-agent-id',
   *   title: 'Support Chat',
   *   welcomeMessage: 'Hello! How can I help you?',
   *   onMessage: (msg) => console.log('Message:', msg)
   * });
   * chat.open();
   * ```
   */
  createTextWidget(options: TextWidgetOptions = {}): TextWidgetHandle {
    return this.text.createWidget(options);
  }

  /**
   * Send a text message (without widget). Calls share one conversation, so
   * each message carries the previous turns as history; resetConversation()
   * starts a fresh one. Separate from any widget's conversation.
   * 
   * @example
   * ```typescript
   * const response = await ai.sendMessage('Hello there', 'your-agent-id');
   * console.log('Bot:', response.messages);
   * ```
   */
  async sendMessage(text: string, agentId?: string) {
    await this.ensureAuthenticated();
    return this.text.sendMessage(text, agentId);
  }

  /**
   * Forget the history sendMessage() has been sending.
   */
  resetConversation(): void {
    this.text.resetConversation();
  }

  // ==================== API ====================

  /**
   * Get all conversation sessions
   * 
   * @example
   * ```typescript
   * const sessions = await ai.getSessions();
   * console.log('Total sessions:', sessions.length);
   * ```
   */
  async getSessions(): Promise<Session[]> {
    await this.ensureAuthenticated();
    return this.api.getSessions();
  }

  /**
   * Get a specific session by ID
   *
   * @example
   * ```typescript
   * const session = await ai.getSession('s1-abc123');
   * console.log('Session turns:', session.turns);
   * ```
   */
  async getSession(id: string): Promise<Session> {
    await this.ensureAuthenticated();
    return this.api.getSession(id);
  }

  /**
   * Delete a session and its transcript.
   */
  async deleteSession(id: string): Promise<void> {
    await this.ensureAuthenticated();
    await this.api.deleteSession(id);
  }

  /**
   * Recent usage records (one per call or answered message), newest first, at
   * most 100. Needs an sk_ key; a pk_ key cannot read usage.
   *
   * @example
   * ```typescript
   * const since = new Date(Date.now() - 7 * 24 * 3600 * 1000);
   * const rows = await ai.getUsage({ from: since });
   * const voiceSeconds = rows
   *   .filter(r => r.type === 'voice')
   *   .reduce((s, r) => s + (r.duration ?? 0), 0);
   * ```
   */
  async getUsage(query: UsageQuery = {}): Promise<UsageRecord[]> {
    await this.ensureAuthenticated();
    return this.api.getUsage(query);
  }

  /**
   * One page of usage records plus the total that match, for building a pager.
   *
   * @example
   * ```typescript
   * const { rows, total } = await ai.getUsagePage({ limit: 10, offset: 10 }); // 11–20
   * ```
   */
  async getUsagePage(query: UsageQuery = {}): Promise<{ rows: UsageRecord[]; total: number }> {
    await this.ensureAuthenticated();
    return this.api.getUsagePage(query);
  }

  /**
   * Totals over every usage row in a period — what billing is computed from.
   * Unlike getUsage() this is not capped at 100 rows.
   *
   * @example
   * ```typescript
   * const month = new Date(); month.setDate(1); month.setHours(0, 0, 0, 0);
   * const t = await ai.getUsageTotals({ from: month });
   * console.log(`${t.voiceCalls} calls, ${Math.round(t.voiceSeconds / 60)} min, ${t.textMessages} messages`);
   * ```
   */
  async getUsageTotals(query: UsageTotalsQuery = {}): Promise<UsageTotals> {
    await this.ensureAuthenticated();
    return this.api.getUsageTotals(query);
  }

  // ==================== Events ====================

  /**
   * Register an event handler
   * 
   * @example
   * ```typescript
   * ai.on('message', (data) => {
   *   console.log('New message:', data);
   * });
   * 
   * ai.on('call-ended', (session) => {
   *   console.log('Call ended:', session);
   * });
   * ```
   */
  on<K extends EventType>(event: K, handler: EventHandler<K>): void {
    super.on(event, handler);
  }

  /**
   * Unregister an event handler
   */
  off<K extends EventType>(event: K, handler: EventHandler<K>): void {
    super.off(event, handler);
  }

  /**
   * Register a one-time event handler
   */
  once<K extends EventType>(event: K, handler: EventHandler<K>): void {
    super.once(event, handler);
  }

  // ==================== Private ====================

  /**
   * Ensure authentication before API calls
   */
  private async ensureAuthenticated(): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      await this.login();
    }
  }
}
